import React, { useState, useEffect } from 'react';
import Header from 'units/dashboard/components/Header';
import { SidebarProvider } from 'units/dashboard/context/SidebarProvider';
import { CheckIcon } from '@heroicons/react/20/solid';
import { ProgressBar } from 'react-loader-spinner';
import Footer from 'units/dashboard/components/Footer';
import UserSidebar from 'units/user/components/UserSidebar';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { checkOfferCode } from '../services/planService';
import AutoFields from './components/AutoFields';
import { useTranslation } from 'react-i18next';

export const PermCategory = {
    PAGE: "page",
    API: "api",
    AI_MODEL: "ai",
    EMBEDDER: "textEmbder",
    CHART: "analysticChart",
    PLATFORM: "platform",
    TOOL: "ai-tool",
};

const groupPermissionsByCategory = (permissions = []) => {
    return permissions.reduce((acc, item) => {
        if (!item.isAvailable) return acc;

        const category = item.category;

        if (!acc[category]) {
            acc[category] = [];
        }

        acc[category].push({
            id: item.id,
            name: item.name,
            label: item.persianName,
            logo: item.logo,
        });

        return acc;
    }, {});
};

/* ============================================
   STARFIELD BACKGROUND
============================================ */
const StarField = () => {
    const [stars, setStars] = useState([]);

    useEffect(() => {
        const generatedStars = Array.from({ length: 60 }, (_, i) => ({
            id: i,
            top: Math.random() * 100,
            left: Math.random() * 100,
            size: Math.random() * 3 + 1,
            delay: Math.random() * 5,
            duration: Math.random() * 3 + 2,
            opacity: Math.random() * 0.7 + 0.3,
        }));
        setStars(generatedStars);
    }, []);

    return (
        <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
            <div
                className="absolute inset-0
                           bg-gradient-to-br
                           from-slate-50
                           via-white
                           to-teal-50/60
                           dark:!from-teal-950
                           dark:!via-gray-950
                           dark:!to-black"
            />

            <div
                className="absolute inset-0 opacity-40
                           bg-gradient-to-tr
                           from-transparent
                           via-amber-50/40
                           to-transparent
                           dark:!from-transparent
                           dark:!via-teal-900/20
                           dark:!to-black/40"
            />

            <div
                className="absolute -top-32 -right-32 w-96 h-96
                           bg-gradient-to-br from-teal-300/40 to-teal-500/20
                           dark:!from-teal-600/30 dark:!to-teal-800/20
                           rounded-full blur-3xl animate-float"
            />

            <div
                className="absolute -bottom-32 -left-32 w-96 h-96
                           bg-gradient-to-br from-amber-300/40 to-amber-500/20
                           dark:!from-amber-600/20 dark:!to-amber-800/10
                           rounded-full blur-3xl animate-float"
                style={{ animationDelay: "500ms" }}
            />

            <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                           w-[600px] h-[600px]
                           bg-gradient-to-br from-teal-200/30 via-amber-100/30 to-teal-200/30
                           dark:!from-teal-800/20 dark:!via-teal-900/15 dark:!to-teal-950/20
                           rounded-full blur-3xl animate-float"
                style={{ animationDelay: "300ms" }}
            />

            {stars.map((star) => (
                <div
                    key={star.id}
                    className="absolute rounded-full
                               bg-gradient-to-br from-amber-400 to-teal-400
                               dark:!from-amber-300 dark:!to-teal-300"
                    style={{
                        top: `${star.top}%`,
                        left: `${star.left}%`,
                        width: `${star.size}px`,
                        height: `${star.size}px`,
                        opacity: star.opacity,
                        animation: `twinkle ${star.duration}s ease-in-out ${star.delay}s infinite`,
                        boxShadow: `0 0 ${star.size * 3}px rgba(212, 175, 55, 0.6)`,
                    }}
                />
            ))}

            <style jsx>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }
                @keyframes twinkle {
                    0%, 100% { opacity: 0.3; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.3); }
                }
                .animate-float {
                    animation: float 3s ease-in-out infinite;
                }
            `}</style>
        </div>
    );
};

export default function PlanCart() {
    const { t, i18n } = useTranslation(["user"]);
    const isEnglish = i18n.language === "en";

    const formatNumber = (num) => {
        const n = Number(num || 0);
        return isEnglish ? n.toLocaleString("en-US") : n.toLocaleString("fa-IR");
    };

    const [loading, setLoading] = useState(false);
    const [discountCode, setDiscountCode] = useState("");
    const [offerCode, setOfferCode] = useState(null);
    const [discountMessage, setDiscountMessage] = useState("");
    const accessToken = useSelector((state) => state.auth.accessToken);
    const location = useLocation();
    const selectedPlan = location.state?.selectedPlan;
    const [planId, setPlanId] = useState(selectedPlan?.id);
    const permissionsByCategory = selectedPlan?.permissions
        ? groupPermissionsByCategory(selectedPlan.permissions)
        : {};
    const [finalPrice, setFinalPrice] = useState(selectedPlan?.finalPrice);
    const [taxFee, setTaxFee] = useState(selectedPlan?.taxFee);
    const navigate = useNavigate();

    if (!selectedPlan) {
        return (
            <>
                <StarField />
                <div className="relative z-10 min-h-screen flex items-center justify-center text-slate-600 dark:!text-zinc-300">
                    {t("planCart.noPlanSelected")}
                </div>
            </>
        );
    }

    const convertToEnglishNumbers = (input) => {
        const persianToEnglishMap = {
            '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4',
            '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9'
        };
        return input.replace(/[۰-۹]/g, (match) => persianToEnglishMap[match]);
    };

    const handleOfferCode = async () => {
        try {
            const planId = selectedPlan.id;
            const code = discountCode.trim();

            const res = await checkOfferCode({
                accessToken,
                code,
                planId
            });

            if (res.ok) {
                setFinalPrice(res?.data);
                setOfferCode(code);
                setDiscountMessage(t("planCart.discountApplied"));
            }
        } catch (error) {
            console.log(error);
        }
    };

    const CATEGORY_LABELS = {
        analysticChart: t("planCart.categoryLabels.analysticChart"),
        ai: t("planCart.categoryLabels.ai"),
        textEmbder: t("planCart.categoryLabels.textEmbder"),
        platform: t("planCart.categoryLabels.platform"),
        "ai-tool": t("planCart.categoryLabels.ai-tool"),
        page: t("planCart.categoryLabels.page"),
    };

    return (
        <>
            <StarField />

            <SidebarProvider>

                {/* Header */}
                <nav
                    className="
                        border-b
                        border-slate-200/70
                        dark:!border-zinc-700
                        dark:!bg-zinc-900/90
                        print:hidden
                        flex items-center
                        fixed top-0 right-0 left-0
                        bg-white/90
                        backdrop-blur-xl
                        z-50
                        shadow-[0_2px_20px_rgba(15,118,110,0.06)]
                    "
                >
                    <Header />
                </nav>

                {/* Sidebar */}
                <div
                    className="
                        vertical-menu
                        rtl:right-0
                        ltr:left-0
                        fixed
                        bottom-0
                        top-16
                        h-screen
                        border-r
                        border-slate-200/70
                        bg-white/90
                        backdrop-blur-xl
                        print:hidden
                        dark:!bg-zinc-900/90
                        dark:!border-zinc-700
                        z-40
                    "
                >
                    <UserSidebar />
                </div>

            </SidebarProvider>

            {loading ? (
                <div
                    className="
                        relative z-10
                        flex justify-center items-center h-screen
                    "
                >
                    <ProgressBar
                        visible={true}
                        height="80"
                        width="80"
                        color="#14b8a6"
                        ariaLabel="progress-bar-loading"
                        wrapperStyle={{}}
                        wrapperClass=""
                    />
                </div>
            ) : (
                <div
                    className="
                        relative
                        z-10
                        flex flex-col
                        min-h-screen
                        my-4
                        justify-center
                        align-items-center
                        overflow-hidden
                    "
                >

                    <div className="main-content flex-grow pt-16 pb-12 relative z-10">


                        {/* Main plan section */}
                        <div
                            className="
                                max-w-[560px]
                                rounded-[1.5rem]
                                bg-white/80
                                dark:!bg-zinc-900/75
                                m-auto
                                mb-4
                                border
                                border-slate-200/80
                                dark:!border-zinc-700
                                shadow-[0_15px_50px_-20px_rgba(15,118,110,0.25)]
                                backdrop-blur-xl
                                overflow-hidden
                                transition-all
                                duration-500
                                hover:shadow-[0_20px_60px_-20px_rgba(15,118,110,0.32)]
                            "
                        >


                            <div className="p-4 sm:p-5">

                                {/* Plan */}
                                <div
                                    className="
                                        rounded-2xl
                                        bg-white/70
                                        dark:!bg-zinc-800/60
                                        p-5 sm:p-6
                                        border
                                        border-slate-200/70
                                        dark:!border-zinc-700
                                        shadow-sm
                                        backdrop-blur-md
                                        transition-all
                                        duration-300
                                        hover:border-teal-200
                                        dark:hover:!border-teal-800
                                    "
                                >

                                    {/* Plan name */}
                                    <div
                                        className="
                                            flex items-center justify-between
                                            gap-3
                                            mb-7
                                            pb-5
                                            border-b
                                            border-slate-100
                                            dark:!border-zinc-700
                                        "
                                    >
                                        <div>
                                            <span
                                                className="
                                                    text-[10px]
                                                    font-bold
                                                    tracking-widest
                                                    text-teal-600
                                                    dark:!text-teal-400
                                                "
                                            >
                                                {t("planCart.planLabel")}
                                            </span>

                                            <h3
                                                className="
                                                    mt-1
                                                    text-[#164e4f]
                                                    dark:!text-teal-300
                                                    text-xl
                                                    sm:text-2xl
                                                    font-bold
                                                "
                                            >
                                                {selectedPlan.name}
                                            </h3>
                                        </div>

                                        <div
                                            className="
                                                flex
                                                items-center
                                                justify-center
                                                w-10 h-10
                                                rounded-xl
                                                bg-gradient-to-br
                                                from-teal-400
                                                to-teal-600
                                                text-white
                                                shadow-lg
                                                shadow-teal-500/20
                                                transition-transform
                                                duration-300
                                                hover:scale-110
                                            "
                                        >
                                            <CheckIcon
                                                className="h-5 w-5"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>

                                    {/* Features */}
                                    <ul
                                        role="list"
                                        className="
                                            mt-4
                                            space-y-1
                                            text-sm
                                            text-slate-600
                                            dark:!text-zinc-300
                                        "
                                    >

                                        {[
                                            t("planCart.features.duration", { count: formatNumber(selectedPlan.duration) }),
                                            t("planCart.features.tokens", { count: formatNumber(selectedPlan.tokens) }),
                                            t("planCart.features.files", { count: formatNumber(selectedPlan.files) }),
                                            t("planCart.features.fileSize", { count: formatNumber(selectedPlan.fileSize) }),
                                            t("planCart.features.userLimit", { count: formatNumber(selectedPlan.userLimit) }),
                                        ].map((text, index) => (
                                            <li
                                                key={index}
                                                className={`
                                                    group
                                                    flex items-start
                                                    gap-x-3
                                                    rounded-xl
                                                    px-2 py-2.5
                                                    transition-all
                                                    duration-300
                                                    hover:bg-teal-50/70
                                                    hover:translate-x-[-2px]
                                                    dark:hover:!bg-teal-950/30
                                                ${isEnglish && 'font-sans'}`}
                                            >
                                                <span
                                                    className="
                                                        mt-0.5
                                                        flex
                                                        h-5 w-5
                                                        shrink-0
                                                        items-center
                                                        justify-center
                                                        rounded-full
                                                        bg-teal-50
                                                        text-teal-600
                                                        transition-all
                                                        duration-300
                                                        group-hover:bg-teal-600
                                                        group-hover:text-white
                                                        dark:!bg-teal-950
                                                        dark:!text-teal-400
                                                    "
                                                >
                                                    <CheckIcon className="h-3.5 w-3.5" />
                                                </span>

                                                <span>{text}</span>
                                            </li>
                                        ))}

                                        {/* Permissions */}
                                        {Object.keys(permissionsByCategory).length > 0 &&
                                            Object.entries(permissionsByCategory).map(([category, items]) => {
                                                if (!items.length) return null;

                                                return (
                                                    <li
                                                        key={category}
                                                        className="mt-4"
                                                    >
                                                        <p
                                                            className="
                                                                mb-2
                                                                flex
                                                                items-center
                                                                gap-x-2
                                                                font-semibold
                                                                text-slate-700
                                                                dark:!text-zinc-200
                                                            "
                                                        >
                                                            <span
                                                                className="
                                                                    flex
                                                                    h-5 w-5
                                                                    items-center
                                                                    justify-center
                                                                    rounded-full
                                                                    bg-gradient-to-br
                                                                    from-teal-600
                                                                    to-teal-800
                                                                    text-white
                                                                "
                                                            >
                                                                <CheckIcon className="h-3.5 w-3.5" />
                                                            </span>

                                                            {CATEGORY_LABELS[category] || category}
                                                        </p>

                                                        <ul className="space-y-1 mr-7">
                                                            {items.map(item => (
                                                                <li
                                                                    key={item.id}
                                                                    className="
                                                                        group
                                                                        flex
                                                                        items-center
                                                                        gap-2
                                                                        rounded-lg
                                                                        px-2 py-2
                                                                        text-xs sm:text-sm
                                                                        transition-all
                                                                        duration-300
                                                                        hover:bg-slate-50
                                                                        hover:text-teal-700
                                                                        dark:hover:!bg-zinc-800
                                                                        dark:hover:!text-teal-300
                                                                    "
                                                                >
                                                                    <span
                                                                        className="
                                                                            transition-transform
                                                                            duration-300
                                                                            group-hover:scale-110
                                                                        "
                                                                    >
                                                                        {item.logo}
                                                                    </span>

                                                                    <span>
                                                                        {item.label}
                                                                    </span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </li>
                                                );
                                            })}

                                        {/* Discount */}
                                        {typeof selectedPlan?.offPercentage === 'number' &&
                                            selectedPlan.offPercentage > 0 && (
                                                <li
                                                    className="
                                                        mt-4
                                                        flex items-center
                                                        gap-x-2
                                                        rounded-xl
                                                        border
                                                        border-amber-200/70
                                                        bg-gradient-to-r
                                                        from-amber-50
                                                        to-orange-50
                                                        px-3 py-2
                                                        text-[#d97706]
                                                        dark:!border-amber-800/50
                                                        dark:!from-amber-950/30
                                                        dark:!to-orange-950/20
                                                        dark:!text-amber-300
                                                    "
                                                >
                                                    <CheckIcon
                                                        className="
                                                            h-5 w-5
                                                            text-amber-500
                                                            flex-none
                                                        "
                                                    />

                                                    <span className="font-semibold">
                                                        {t("planCart.features.discount", {
                                                            percent: formatNumber(selectedPlan.offPercentage),
                                                        })}
                                                    </span>

                                                    <span className="mr-auto text-amber-400">
                                                        ✦
                                                    </span>
                                                </li>
                                            )}
                                    </ul>
                                </div>

                                {/* Price */}
                                <div className="mt-4">

                                    <div
                                        className="
                                            w-full
                                            text-center
                                            flex
                                            justify-between
                                            items-center
                                            ps-4
                                            rounded-xl
                                            bg-white/80
                                            dark:!bg-zinc-800/70
                                            border
                                            border-slate-200/70
                                            dark:!border-zinc-700
                                            shadow-sm
                                            overflow-hidden
                                            transition-all
                                            duration-300
                                            hover:border-teal-200
                                            dark:hover:!border-teal-800
                                        "
                                    >
                                        <span
                                            className={`
                                                py-3
                                                font-bold
                                                text-sm
                                                text-slate-700
                                                dark:!text-zinc-200
                                            ${isEnglish && 'font-sans'}`}
                                        >
                                            {t("planCart.planPrice")}
                                        </span>

                                        <span
                                            className={`
                                                flex
                                                items-center
                                                px-4 py-3
                                                font-bold
                                                text-sm
                                                text-teal-800
                                                bg-gradient-to-l
                                                from-teal-100
                                                via-teal-50
                                                to-amber-50
                                                dark:!from-teal-950
                                                dark:!via-teal-900/50
                                                dark:!to-amber-950/40
                                                dark:!text-teal-300
                                            ${isEnglish && 'font-sans'}`}
                                        >
                                            {typeof selectedPlan?.offPercentage === 'number' &&
                                                selectedPlan.offPercentage > 0
                                                ? formatNumber(
                                                    selectedPlan.price *
                                                    (1 - selectedPlan.offPercentage / 100)
                                                )
                                                : formatNumber(selectedPlan?.price)
                                            }

                                            <span className="mr-1 text-[10px] font-medium opacity-70 px-1">
                                                {t("planCart.currency")}
                                            </span>
                                        </span>
                                    </div>
                                </div>

                                {/* Discount Code */}
                                <div className="mt-4">

                                    <div
                                        className="
                                            flex
                                            flex-wrap
                                            sm:flex-row
                                            justify-center
                                            items-stretch
                                            sm:items-center
                                            p-1.5
                                            gap-2
                                            rounded-xl
                                            border
                                            border-slate-200/80
                                            dark:!border-zinc-700
                                            bg-white/70
                                            dark:!bg-zinc-800/60
                                            shadow-sm
                                            backdrop-blur
                                            transition-all
                                            duration-300
                                            focus-within:border-teal-400
                                            focus-within:ring-4
                                            focus-within:ring-teal-500/10
                                        "
                                    >
                                        <input
                                            type="text"
                                            placeholder={t("planCart.discountPlaceholder")}
                                            value={discountCode}
                                            onChange={(e) =>
                                                setDiscountCode(
                                                    convertToEnglishNumbers(e.target.value)
                                                )
                                            }
                                            className={`
                                                flex-1
                                                min-w-0
                                                bg-transparent
                                                border-0
                                                px-4 py-2.5
                                                text-center
                                                text-sm
                                                text-slate-700
                                                dark:!text-zinc-100
                                                placeholder:text-slate-400
                                                dark:!placeholder:text-zinc-500
                                                outline-none
                                            ${isEnglish && 'font-sans'} `}
                                        />

                                        <button
                                            onClick={handleOfferCode}
                                            className="
                                                relative
                                                overflow-hidden
                                                bg-gradient-to-l
                                                from-teal-600
                                                to-teal-400
                                                text-white
                                                px-5 py-2.5
                                                rounded-lg
                                                w-full
                                                sm:w-auto
                                                font-semibold
                                                text-sm
                                                shadow-md
                                                shadow-teal-500/20
                                                transition-all
                                                duration-300
                                                hover:from-teal-600
                                                hover:to-teal-600
                                                hover:shadow-lg
                                                hover:shadow-teal-500/25
                                                hover:-translate-y-0.5
                                                active:translate-y-0
                                            "
                                        >
                                            {t("planCart.check")}
                                        </button>
                                    </div>

                                    {discountMessage && (
                                        <span
                                            className="
                                                block
                                                px-4 mt-2
                                                text-amber-600
                                                dark:!text-amber-400
                                                text-xs
                                                font-medium
                                            "
                                        >
                                            ✦ {discountMessage}
                                        </span>
                                    )}
                                </div>

                                {/* Final Price */}
                                <div className="my-8">

                                    <div
                                        className="
                                            w-full
                                            text-center
                                            flex
                                            justify-between
                                            items-center
                                            ps-4
                                            rounded-xl
                                            bg-gradient-to-r
                                            from-white
                                            via-teal-50/50
                                            to-amber-50/60
                                            dark:!from-zinc-800
                                            dark:!via-teal-950/30
                                            dark:!to-amber-950/20
                                            border
                                            border-teal-100
                                            dark:!border-teal-900/60
                                            shadow-sm
                                            overflow-hidden
                                            transition-all
                                            duration-300
                                            hover:shadow-md
                                        "
                                    >
                                        <span
                                            className={`
                                                py-3
                                                font-bold
                                                text-xs sm:text-sm
                                                text-slate-700
                                                dark:!text-zinc-200 px-1
                                            ${isEnglish && 'font-sans'}`}
                                        >
                                            {t("planCart.finalPriceWithTax", {
                                                tax: formatNumber(taxFee),
                                            })}
                                        </span>

                                        <span
                                            className={`
                                                flex
                                                items-center
                                                px-4 py-3
                                                bg-gradient-to-l
                                                from-teal-600
                                                to-teal-500
                                                text-white
                                                font-black
                                                text-sm
                                                shadow-inner
                                            ${isEnglish && 'font-sans'}`}
                                        >
                                            {formatNumber(finalPrice)}

                                            <span className="mr-1 text-[10px] opacity-80 px-1">
                                                {t("planCart.currency")}
                                            </span>
                                        </span>
                                    </div>
                                </div>

                                {/* Continue Button */}
                                <button
                                    onClick={() =>
                                        navigate('/payment-method', {
                                            state: {
                                                finalPrice,
                                                offerCode,
                                                planId
                                            }
                                        })
                                    }
                                    className="
                                        group
                                        relative
                                        overflow-hidden
                                        mt-10
                                        w-full
                                        rounded-xl
                                        bg-gradient-to-l
                                        from-teal-700
                                        via-teal-600
                                        to-teal-500
                                        px-4 py-3
                                        text-center
                                        text-sm
                                        font-bold
                                        text-white
                                        shadow-lg
                                        shadow-teal-500/20
                                        transition-all
                                        duration-300
                                        hover:-translate-y-1
                                        hover:shadow-xl
                                        hover:shadow-teal-500/30
                                        active:translate-y-0
                                    "
                                >
                                    {/* Shine animation */}
                                    <span
                                        className="
                                            absolute
                                            inset-y-0
                                            -left-1/2
                                            w-1/3
                                            skew-x-[-20deg]
                                            bg-white/20
                                            transition-all
                                            duration-700
                                            group-hover:left-[120%]
                                        "
                                    />

                                    <span className="relative flex items-center justify-center gap-2">
                                        {t("planCart.confirmAndContinue")}

                                        <span
                                            className="
                                                text-lg
                                                transition-transform
                                                duration-300
                                                group-hover:-translate-x-1
                                            "
                                        >
                                            ←
                                        </span>
                                    </span>
                                </button>

                            </div>
                        </div>

                        <Footer />
                    </div>
                </div>
            )}
        </>
    );
}